import { FC, useCallback } from "react"
import { Helmet } from 'react-helmet-async'
import { Box, Button, Card, Container, FormHelperText, Grid, Typography, useTheme } from "@mui/material";

import { useFormik } from "formik";
import * as Yup from 'yup'
import { SaveAction } from "@/dataProvider/types";
import { ChangePasswordInput } from "@/gen/api/user";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useDispatch } from "react-redux";
import { goBack } from "connected-react-router";
import { useSnackbar } from "notistack";
import { RecPatch, SupportedResources } from "@/dataProvider/tools";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useCreate } from "ra-core";
import chainOptions from "@/utils/chainOptions";
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { rPassword } from "./LoginContext";


const PasswordChange: FC = () => {
  const resource: SupportedResources = 'changepassword'
  useResourceRegister({ name: resource })

  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [sendChange,] = useCreate<ChangePasswordInput & RecPatch>(resource)

  const save: SaveAction<ChangePasswordInput> = useCallback((input, redirect, options) => {
    sendChange(resource, input, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Successfully changed your password', { variant: 'success' })
        dispatch(goBack())
      }
    }))
  }, [enqueueSnackbar, dispatch, sendChange]);

  const cancel = useCallback(() => {
    dispatch(goBack())
  }, [dispatch]);
  return (
    <PasswordChangeBody 
      save={save}
      cancel={cancel}
    />
  )
}



export interface PasswordChangeProps {
  save: SaveAction<ChangePasswordInput>;
  cancel(): void;
}

const validationSchema = 
  Yup.object().shape({
    originalPassword: Yup.string().required('Original Password is required'),
    password: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .required('Password is required'),
    confirmPassword: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm Password is required'),
  })

export const PasswordChangeBody: FC<PasswordChangeProps> = ({ save, cancel }) => {
  const theme = useTheme()
  const initialValues = {
    originalPassword: '',
    password: '',
    confirmPassword: '',
    submit: null
  }
  
  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setSubmitting,
    setErrors,
  }) => {
    setSubmitting(true);
    save(values, '', {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err) => {
        setStatus({ success: false })
        setSubmitting(false)
        setErrors({ submit: retrieveAxiosErr(err) })
      },
    })
  }, [save])
  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm } = formikCtrl

  return (
    <>
      <Helmet>
        <title>Change Password | OnData</title>
      </Helmet>
      <Box sx={{ height: '100%', overflowY: 'auto', }} >
        <Container maxWidth="lg" sx={{ py: '20px' }} >
          <Typography
            color="textPrimary"
            sx={{ fontWeight: 'fontWeightBold', textAlign: 'center', py: 3, }}
            variant="h5"
          >
            Change My Password
          </Typography>

          <Card
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                lg: 'repeat(2, 1fr)',
                xs: 'repeat(1, 1fr)'
              },
              minHeight: '100%'
            }}
          >
            <Box sx={{ pt: 8, }} >
              <Container
                maxWidth="md"
                sx={{ pl: { lg: 15 } }}
              >
                <Typography
                  sx={{ color: 'primary.main', pb: 3 }}
                  variant="h6"
                >
                  Password Guidelines
                </Typography>
                <Typography
                  color="textPrimary"
                  sx={{ py: 0.5 }}
                  variant="body1"
                >
                  1. Password must be 12 or more characters long.
                </Typography>
                <Typography
                  color="textPrimary"
                  sx={{ py: 0.5 }}
                  variant="body1"
                >
                  2. Password must contain uppercase and lowercase characters, numbers and special characters.
                </Typography>
              </Container>
            </Box>
            <Box sx={{ backgroundColor: 'background.paper', pt: 8, pb: 3, }} >
              <Container
                maxWidth="md"
                sx={{ pr: { lg: 8 } }}
              >
                <Box>
                  <OdForm formikCtrl={formikCtrl}>
                    <TextField
                      label="Original Password"
                      margin="normal"
                      name="originalPassword"
                      type="password"
                      />
                    <TextField
                      label="Password"
                      margin="normal"
                      name="password"
                      type="password"
                      />
                    <TextField
                      label="Confirm Password"
                      margin="normal"
                      name="confirmPassword"
                      type="password"
                    />
                    {errors.submit && (
                      <Box sx={{ mt: 1 }}>
                        <FormHelperText error>
                          {errors.submit}
                        </FormHelperText>
                      </Box>
                    )}
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} >
                          <Button
                            color={theme.palette.mode === 'light' ? "inherit" : 'primary'}
                            disabled={isSubmitting}
                            fullWidth
                            size="large"
                            type="button"
                            variant={theme.palette.mode === 'light' ? "contained" : 'outlined'}
                            onClick={() => {
                              resetForm()
                              cancel()
                            }}
                          >
                            Cancel
                          </Button>

                        </Grid>
                        <Grid item xs={6} >
                          <Button
                            color="primary"
                            disabled={isSubmitting}
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                          >
                            Submit
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </OdForm>
                </Box>
              </Container>
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  )
}

export default PasswordChange;
